import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, Divider, Chip, Stack, TextField,
  Accordion, AccordionSummary, AccordionDetails, LinearProgress
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShareIcon from '@mui/icons-material/Share';
import BlockIcon from '@mui/icons-material/Block';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import CallRiskSection from './CallRiskSection';
import CallAiContextSection from './CallAiContextSection';
import {
  getCallSummary, generateCallSummaryApi, saveFinalSummary,
  generateDualSummary, signTherapistNote, shareClientSummary, withholdClientSummary,
  getQaSelfReview
} from '../../services/callsApi';

function formatDuration(sec) {
  if (sec == null) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function Block({ label, text }) {
  if (!text) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>{text}</Typography>
    </Box>
  );
}

/**
 * AI-drafted call summary (SOAP + freeform + themes + risks) with a
 * therapist-editable "final" version. Raw AI output is preserved.
 */
// Plain-English reasons from the client-summary gate (AD-54). A therapist saw
// the raw code "profile_not_enabled" for a client in an AI-led service group.
const DUAL_SUMMARY_REASONS = {
  profile_not_enabled: "Client summaries are only available for therapist-led and hybrid services, and this client's service group is not one of them.",
  no_service_group: 'This client is not in an active service group, so a client summary cannot be generated.'
};
function dualSummaryReasonText(reason) {
  if (!reason) return 'No summary was returned. Please try again.';
  return DUAL_SUMMARY_REASONS[reason] || `The client summary could not be generated (${reason}).`;
}

export default function CallSummaryDialog({ open, onClose, token, liveCallId, callMeta }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [finalSummary, setFinalSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  // Spec E — dual summary lifecycle (therapist-led mode)
  const [clientSummary, setClientSummary] = useState(null);  // { text, generatedAt, model }
  const [summaryLifecycle, setSummaryLifecycle] = useState(null);
  const [aiContext, setAiContext] = useState(null);
  const [dualBusy, setDualBusy] = useState(null);  // 'generate' | 'sign' | 'share' | 'withhold'
  const [withholdReason, setWithholdReason] = useState('');
  const [withholdOpen, setWithholdOpen] = useState(false);
  const [dualNotice, setDualNotice] = useState(null);

  // Spec F — QA self-review (lazy)
  const [qaScores, setQaScores] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState(null);

  useEffect(() => {
    if (!open || !liveCallId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getCallSummary({ token, liveCallId });
        if (cancelled) return;
        setAiSummary(data.aiSummary || null);
        setFinalSummary(data.finalSummary || null);
        setDraft(data.finalSummary?.text || data.aiSummary?.freeform || '');
        setClientSummary(data.clientFacingSummary || null);
        setSummaryLifecycle(data.summaryLifecycle || null);
        setAiContext(data.aiContext || null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.msg || 'Failed to load summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, liveCallId, token]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateCallSummaryApi({ token, liveCallId });
      setAiSummary(res.aiSummary || null);
      setFinalSummary(res.finalSummary || null);
      setDraft(res.finalSummary?.text || res.aiSummary?.freeform || '');
    } catch (err) {
      setError(err?.response?.data?.msg || err.message || 'Summary generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveFinal = async () => {
    setSaving(true);
    try {
      await saveFinalSummary({ token, liveCallId, text: draft });
      setFinalSummary({ text: draft, editedAt: new Date() });
      setEditing(false);
    } catch (err) {
      setError(err?.response?.data?.msg || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Spec E — dual summary actions
  async function handleGenerateClientSummary() {
    setDualBusy('generate');
    setDualNotice(null);
    try {
      const res = await generateDualSummary({ token, liveCallId, force: !!clientSummary?.text });
      if (res?.ok && res.content) {
        setClientSummary({ text: res.content, generatedAt: res.generatedAt || new Date(), model: res.model });
        setDualNotice({ severity: 'success', text: 'Client-facing summary generated.' });
      } else {
        setDualNotice({ severity: 'warning', text: dualSummaryReasonText(res?.reason) });
      }
    } catch (err) {
      setDualNotice({ severity: 'error', text: err?.response?.data?.msg || err.message || 'Generate failed' });
    } finally {
      setDualBusy(null);
    }
  }

  async function handleSignNote() {
    setDualBusy('sign');
    try {
      const res = await signTherapistNote({ token, liveCallId });
      if (res?.ok) {
        setSummaryLifecycle((prev) => ({ ...(prev || {}), therapistNoteStatus: 'signed', signedAt: res.signedAt }));
        setDualNotice({ severity: 'success', text: 'Note signed as final.' });
      }
    } catch (err) {
      setDualNotice({ severity: 'error', text: err?.response?.data?.msg || err.message || 'Sign failed' });
    } finally {
      setDualBusy(null);
    }
  }

  async function handleShare() {
    setDualBusy('share');
    try {
      const res = await shareClientSummary({ token, liveCallId });
      if (res?.ok) {
        setSummaryLifecycle((prev) => ({ ...(prev || {}), clientSummaryStatus: 'shared', sharedAt: res.sharedAt }));
        setDualNotice({ severity: 'success', text: 'Summary shared with the client.' });
      } else {
        setDualNotice({ severity: 'warning', text: res?.reason || 'Could not share.' });
      }
    } catch (err) {
      setDualNotice({ severity: 'error', text: err?.response?.data?.msg || err.message || 'Share failed' });
    } finally {
      setDualBusy(null);
    }
  }

  async function handleWithhold() {
    if (!withholdReason.trim()) {
      setDualNotice({ severity: 'warning', text: 'Withhold reason is required.' });
      return;
    }
    setDualBusy('withhold');
    try {
      const res = await withholdClientSummary({ token, liveCallId, reason: withholdReason.trim() });
      if (res?.ok) {
        setSummaryLifecycle((prev) => ({
          ...(prev || {}),
          clientSummaryStatus: 'withheld',
          withheldAt: res.withheldAt,
          withholdReason: withholdReason.trim()
        }));
        setWithholdOpen(false);
        setWithholdReason('');
        setDualNotice({ severity: 'info', text: 'Summary withheld; not visible to the client.' });
      }
    } catch (err) {
      setDualNotice({ severity: 'error', text: err?.response?.data?.msg || err.message || 'Withhold failed' });
    } finally {
      setDualBusy(null);
    }
  }

  // Spec F — lazy-load QA self-review on accordion expand
  async function loadQa() {
    if (qaScores || qaLoading) return;
    setQaLoading(true);
    setQaError(null);
    try {
      const res = await getQaSelfReview({ token, liveCallId });
      setQaScores(res?.scores || []);
    } catch (err) {
      setQaError(err?.response?.data?.msg || err.message || 'Could not load QA review');
    } finally {
      setQaLoading(false);
    }
  }

  const hasAI = !!aiSummary;
  const noteSigned = summaryLifecycle?.therapistNoteStatus === 'signed';
  const summaryShared = summaryLifecycle?.clientSummaryStatus === 'shared';
  const summaryWithheld = summaryLifecycle?.clientSummaryStatus === 'withheld';

  return (
    <Dialog open={open} onClose={editing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Call summary
        {callMeta?.createdAt && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {new Date(callMeta.createdAt).toLocaleString()}
            {callMeta.durationSec != null && ` · ${formatDuration(callMeta.durationSec)}`}
            {aiSummary?.model && ` · ${aiSummary.model}`}
          </Typography>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ minHeight: 320, maxHeight: '65vh' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !hasAI && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No summary has been generated for this call yet.
            </Typography>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            >
              {generating ? 'Generating…' : 'Generate summary'}
            </Button>
          </Box>
        )}

        {!loading && hasAI && (
          <>
            <CallRiskSection aiSummary={aiSummary} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Therapist's final summary</Typography>
            {editing ? (
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                multiline
                minRows={5}
                maxRows={12}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="Refine the summary as needed."
              />
            ) : (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {finalSummary?.text || <em style={{ color: '#777' }}>No therapist-edited version yet. Shows AI draft below.</em>}
                </Typography>
                {finalSummary?.editedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    edited {new Date(finalSummary.editedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>AI draft (SOAP format)</Typography>

            <Block label="Subjective" text={aiSummary.subjective} />
            <Block label="Objective"  text={aiSummary.objective} />
            <Block label="Assessment" text={aiSummary.assessment} />
            <Block label="Plan"       text={aiSummary.plan} />

            {aiSummary.freeform && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Block label="Freeform" text={aiSummary.freeform} />
              </>
            )}

            {aiSummary.themes?.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, gap: 0.5 }}>
                {aiSummary.themes.map((t, i) => (
                  <Chip key={i} label={t} size="small" variant="outlined" />
                ))}
              </Stack>
            )}

            {aiSummary.generatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                AI generated {new Date(aiSummary.generatedAt).toLocaleString()}
              </Typography>
            )}

            {/* Spec E — Therapist-Led Dual summary lifecycle */}
            <Divider sx={{ my: 3 }} />
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ShareIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>Client-facing summary</Typography>
                  {noteSigned && <Chip label="note signed" size="small" color="success" />}
                  {summaryShared && <Chip label="shared" size="small" color="primary" />}
                  {summaryWithheld && <Chip label="withheld" size="small" />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {dualNotice && (
                  <Alert severity={dualNotice.severity} onClose={() => setDualNotice(null)} sx={{ mb: 2 }}>
                    {dualNotice.text}
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Plain-language recap intended to be shared with the client. The SOAP note above stays clinic-internal.
                  Generated for service groups configured as therapist-led / hybrid / therapist-enhanced.
                </Typography>

                {clientSummary?.text ? (
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{clientSummary.text}</Typography>
                    {clientSummary.generatedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        generated {new Date(clientSummary.generatedAt).toLocaleString()}
                        {clientSummary.model ? ` · ${clientSummary.model}` : ''}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Not yet generated. Click <strong>Generate</strong> to draft it from the cleaned transcript.
                  </Alert>
                )}

                {summaryLifecycle?.withholdReason && summaryWithheld && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Withheld: {summaryLifecycle.withholdReason}
                  </Alert>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  <Button
                    size="small" variant="contained"
                    onClick={handleGenerateClientSummary}
                    disabled={!!dualBusy}
                    startIcon={dualBusy === 'generate' ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                  >
                    {clientSummary?.text ? 'Regenerate' : 'Generate'}
                  </Button>
                  <Button
                    size="small" variant="outlined" color="success"
                    onClick={handleSignNote}
                    disabled={!!dualBusy || noteSigned}
                    startIcon={dualBusy === 'sign' ? <CircularProgress size={14} /> : <VerifiedIcon />}
                  >
                    {noteSigned ? 'Note signed' : 'Sign note as final'}
                  </Button>
                  <Button
                    size="small" variant="outlined" color="primary"
                    onClick={handleShare}
                    disabled={!!dualBusy || !clientSummary?.text || summaryShared}
                    startIcon={dualBusy === 'share' ? <CircularProgress size={14} /> : <ShareIcon />}
                  >
                    {summaryShared ? 'Shared with client' : 'Share with client'}
                  </Button>
                  <Button
                    size="small" variant="outlined" color="warning"
                    onClick={() => setWithholdOpen((v) => !v)}
                    disabled={!!dualBusy}
                    startIcon={<BlockIcon />}
                  >
                    {summaryWithheld ? 'Update withhold' : 'Withhold'}
                  </Button>
                </Stack>

                {withholdOpen && (
                  <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'warning.light', borderRadius: 1 }}>
                    <TextField
                      label="Reason for withholding"
                      placeholder="e.g. Client requested no written record this session"
                      multiline minRows={2} fullWidth
                      value={withholdReason}
                      onChange={(e) => setWithholdReason(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => { setWithholdOpen(false); setWithholdReason(''); }}>Cancel</Button>
                      <Button size="small" variant="contained" color="warning"
                        onClick={handleWithhold}
                        disabled={dualBusy === 'withhold' || !withholdReason.trim()}
                      >
                        Confirm withhold
                      </Button>
                    </Stack>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
            <CallAiContextSection token={token} liveCallId={liveCallId} aiContext={aiContext} />

            {/* Spec F — Therapist self-review of QA scores (lazy-loaded) */}
            <Accordion onChange={(_, expanded) => expanded && loadQa()}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <GavelIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>Quality review</Typography>
                  {qaScores && qaScores.length > 0 && (
                    <Chip size="small" label={`${qaScores.length} rubric${qaScores.length === 1 ? '' : 's'}`} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Your full-detail self-review with rationale + supporting evidence. Supervisors see a redacted version.
                </Typography>
                {qaLoading && <LinearProgress sx={{ mb: 2 }} />}
                {qaError && <Alert severity="error" sx={{ mb: 2 }}>{qaError}</Alert>}

                {qaScores && qaScores.length === 0 && (
                  <Alert severity="info">No rubric scores yet. Auto-scoring runs after end-of-call for therapist-led service groups.</Alert>
                )}

                {qaScores && qaScores.map((s) => (
                  <Box key={s.rubricKey} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {s.rubricKey}
                        {s.rubricVersion ? ` (v${s.rubricVersion})` : ''}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {s.scoringFailed || typeof s.weightedTotal !== 'number' ? (
                          // Nothing was scored (PD-09); never shown as 0
                          <Chip size="small" label="Scoring failed" color="default" variant="outlined" />
                        ) : (
                          <Chip size="small" label={`${Math.round(s.weightedTotal)}/100`} color={s.weightedTotal >= 70 ? 'success' : s.weightedTotal >= 50 ? 'warning' : 'error'} />
                        )}
                        {s.criticalFlags?.filter(f => f.label !== 'scoring_failed').length > 0 && (
                          <Chip size="small" label={`${s.criticalFlags.filter(f => f.label !== 'scoring_failed').length} critical`} color="error" />
                        )}
                      </Stack>
                    </Stack>
                    {s.strengthsSummary && <Typography variant="body2" sx={{ mb: 1 }}><strong>Strengths:</strong> {s.strengthsSummary}</Typography>}
                    {s.improvementSummary && <Typography variant="body2" sx={{ mb: 1 }}><strong>For improvement:</strong> {s.improvementSummary}</Typography>}

                    {(s.dimensions || []).map((d) => (
                      <Box key={d.key} sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderColor: 'grey.300' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {d.name} — {d.score}/{d.maxScore}
                        </Typography>
                        {d.rationale && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                            {d.rationale}
                          </Typography>
                        )}
                        {d.evidenceExcerpts?.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {d.evidenceExcerpts.map((e, i) => (
                              <Typography key={i} variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                                “{e}”
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {hasAI && !editing && (
          <Button onClick={() => setEditing(true)}>Edit summary</Button>
        )}
        {hasAI && !editing && (
          <Button onClick={handleGenerate} disabled={generating} size="small">
            Regenerate
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {editing ? (
          <>
            <Button onClick={() => { setEditing(false); setDraft(finalSummary?.text || aiSummary?.freeform || ''); }} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveFinal}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
